import React, { useEffect, useState } from 'react';
import { GainsMonitorService } from '../services/gainsMonitorService';
import type { KaizenRecord, MetricsSnapshot } from '../types/gainsMonitor';
import { parseMetricInput } from '../utils/kaizenMetricSemantics';
import {
  EnterpriseModal,
  ModalField,
  ModalPrimaryButton,
  ModalSecondaryButton,
  modalInputClass,
} from './layout/EnterpriseModal';

function measuredOrEmpty(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Throughput, defeitos, retrabalho e amostra nasciam como 0, 0, 0 e 1
 * no estado do formulário. As colunas também têm default 0/1 no schema
 * histórico. Campo vazio segue como null para o insert não aplicar esse
 * default. Zero digitado continua 0. Target 0 gravado no Kaizen continua
 * visível: pode ser zero real ou default antigo, e esta tela não converte.
 */
function TargetHint({ value, unit }: { value: number | null | undefined; unit: string }) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return <p className="mt-0.5 text-[10px] text-[var(--text-muted)]">Target: {value} {unit}</p>;
}

interface SnapshotFormProps {
  kaizen: KaizenRecord;
  onClose: () => void;
  onSuccess: () => void;
}

export const SnapshotForm: React.FC<SnapshotFormProps> = ({ kaizen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    measured_at: new Date().toISOString().split('T')[0],
    lead_time: measuredOrEmpty(kaizen.current_lead_time),
    cycle_time: measuredOrEmpty(kaizen.current_cycle_time),
    ftr: measuredOrEmpty(kaizen.current_ftr),
    capacity_fte: measuredOrEmpty(kaizen.current_capacity_fte),
    throughput: null as number | null,
    defect_rate: null as number | null,
    rework_rate: null as number | null,
    sample_size: null as number | null,
    notes: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      if (formData.lead_time === null || formData.lead_time === undefined) {
        setError('Informe o lead time medido. Campo vazio não vira zero.');
        setSaving(false);
        return;
      }
      const saved = await GainsMonitorService.addSnapshot({
        kaizen_id: kaizen.id,
        measured_at: new Date(formData.measured_at),
        lead_time: formData.lead_time,
        cycle_time: formData.cycle_time,
        ftr: formData.ftr,
        capacity_fte: formData.capacity_fte,
        throughput: formData.throughput,
        defect_rate: formData.defect_rate,
        rework_rate: formData.rework_rate,
        sample_size: formData.sample_size,
        notes: formData.notes,
      } as Partial<MetricsSnapshot>);
      if (!saved) {
        setError('Não foi possível salvar a medição.');
        return;
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar medição');
    } finally {
      setSaving(false);
    }
  };

  const leadDeviation = formData.lead_time !== null
    && formData.lead_time !== undefined
    && kaizen.target_lead_time !== null
    && kaizen.target_lead_time !== undefined
    && Number.isFinite(kaizen.target_lead_time)
    && kaizen.target_lead_time !== 0
    ? ((formData.lead_time - kaizen.target_lead_time) / kaizen.target_lead_time) * 100
    : null;

  const sectionClass = 'mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]';

  return (
    <EnterpriseModal
      open
      onClose={onClose}
      title="Adicionar medição"
      subtitle={kaizen.title}
      loading={saving}
      maxWidth="max-w-3xl"
      footer={(
        <>
          <ModalSecondaryButton onClick={onClose} disabled={saving}>Cancelar</ModalSecondaryButton>
          <ModalPrimaryButton onClick={() => { void handleSubmit(); }} loading={saving}>Salvar medição</ModalPrimaryButton>
        </>
      )}
    >
      {error && (
        <p className="mb-3 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-600">{error}</p>
      )}

      <p className={sectionClass}>Data da medição</p>
      <ModalField label="Data">
        <input
          type="date"
          required
          value={formData.measured_at}
          onChange={(event) => setFormData({ ...formData, measured_at: event.target.value })}
          className={modalInputClass}
        />
      </ModalField>

      <p className={`${sectionClass} mt-2`}>Métricas principais</p>
      <div className="grid grid-cols-2 gap-x-3">
        <ModalField label="Lead Time (min) *">
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="min"
            value={formData.lead_time ?? ''}
            onChange={(event) => setFormData({ ...formData, lead_time: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
          <TargetHint value={kaizen.target_lead_time} unit="min" />
          {leadDeviation !== null && (
            <p className="text-[10px] text-[var(--text-muted)]">{leadDeviation.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% em relação à meta</p>
          )}
        </ModalField>
        <ModalField label="Cycle Time (min)">
          <input
            type="number"
            step="0.01"
            placeholder="min"
            value={formData.cycle_time ?? ''}
            onChange={(event) => setFormData({ ...formData, cycle_time: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
          <TargetHint value={kaizen.target_cycle_time} unit="min" />
        </ModalField>
        <ModalField label="FTR (%)">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="%"
            value={formData.ftr ?? ''}
            onChange={(event) => setFormData({ ...formData, ftr: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
          <TargetHint value={kaizen.target_ftr} unit="%" />
        </ModalField>
        <ModalField label="Capacidade (FTE)">
          <input
            type="number"
            step="0.01"
            placeholder="FTE"
            value={formData.capacity_fte ?? ''}
            onChange={(event) => setFormData({ ...formData, capacity_fte: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
          <TargetHint value={kaizen.target_capacity_fte} unit="FTE" />
        </ModalField>
      </div>

      <p className={`${sectionClass} mt-2`}>Métricas adicionais</p>
      <div className="grid grid-cols-2 gap-x-3">
        <ModalField label="Throughput (unidades/dia)">
          <input
            type="number"
            step="0.01"
            placeholder="unidades/dia"
            value={formData.throughput ?? ''}
            onChange={(event) => setFormData({ ...formData, throughput: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
        </ModalField>
        <ModalField label="Taxa de Defeitos (%)">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="%"
            value={formData.defect_rate ?? ''}
            onChange={(event) => setFormData({ ...formData, defect_rate: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
        </ModalField>
        <ModalField label="Taxa de Retrabalho (%)">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="%"
            value={formData.rework_rate ?? ''}
            onChange={(event) => setFormData({ ...formData, rework_rate: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
        </ModalField>
        <ModalField label="Tamanho da Amostra">
          <input
            type="number"
            step="1"
            placeholder="amostra"
            value={formData.sample_size ?? ''}
            onChange={(event) => setFormData({ ...formData, sample_size: parseMetricInput(event.target.value) })}
            className={modalInputClass}
          />
        </ModalField>
      </div>

      <p className={`${sectionClass} mt-2`}>Observações</p>
      <textarea
        rows={2}
        value={formData.notes}
        onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
        placeholder="Adicione observações sobre esta medição..."
        className={modalInputClass}
      />
    </EnterpriseModal>
  );
};
